import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import mixpanel from 'mixpanel-browser';
import { useEffect, useRef, useState } from 'react';

import {
  selectImpersonatedUserId,
  useImpersonationSelector,
} from '@revelio/auth';
import {
  jsonKeysWithDifferentValues,
  PageNavTrackingEvents,
  StorageKind,
  StorageSubscribe,
} from '@revelio/core';

import { allPreferencesQuery } from '../api/all-preferences-query';
import { savePreferencesForScope } from '../api/user-preferences-api';
import {
  PreferenceScope,
  UserPreferenceKey,
  type UserPreferences,
} from '../types';
import { getDefaultPreferences } from '../utils/preference-defaults';
import { filterPreferencesForScope } from '../utils/preference-helpers';
import { getPreferenceSavedSyncKey } from '../utils/preference-operations';
import { PreferenceBrowserStorage } from '../utils/preference-session-storage';
import { trackablePreferenceValues } from '../utils/preference-tracking';
import { normalizePreferencesForDiff } from '../utils/preferences-normalize';

export interface UseUserPreferencesReturn {
  preferences: UserPreferences;
  savedPreferences: UserPreferences;
  isInitialLoading: boolean;
  isSyncing: boolean;
  error: Error | null;
  syncWithServer: () => Promise<void>;
}

/**
 * A top-level spread replaces the whole `taxonomies` object, so a record saved
 * before a taxonomy sub-key existed (e.g. `activity`) would erase that key's
 * coded default — and every diff against the defaults would then report a
 * phantom "unsaved" change forever. Merge the taxonomies key per-field instead.
 */
const mergePreferenceLayers = (
  defaults: UserPreferences,
  ...layers: Partial<UserPreferences>[]
): UserPreferences => {
  const merged = Object.assign({}, defaults, ...layers) as UserPreferences;
  merged[UserPreferenceKey.Taxonomies] = Object.assign(
    {},
    defaults[UserPreferenceKey.Taxonomies],
    ...layers.map((layer) => layer[UserPreferenceKey.Taxonomies])
  );
  return merged;
};

const getLocalPreferences = (scope: PreferenceScope): UserPreferences => {
  const stored = PreferenceBrowserStorage.get(scope);
  const storedGlobal = PreferenceBrowserStorage.get(PreferenceScope.GLOBAL);
  const defaults = getDefaultPreferences(scope);
  return mergePreferenceLayers(defaults, storedGlobal, stored);
};

type AllServerPreferences = Record<PreferenceScope, Partial<UserPreferences>>;

const computeSavedPreferences = (
  scope: PreferenceScope,
  allServerPrefs: AllServerPreferences | undefined
): UserPreferences => {
  const defaults = getDefaultPreferences(scope);
  if (!allServerPrefs) return defaults;
  return mergePreferenceLayers(
    defaults,
    allServerPrefs[PreferenceScope.GLOBAL],
    allServerPrefs[scope]
  );
};

export function useUserPreferences(
  scope: PreferenceScope
): UseUserPreferencesReturn {
  const queryClient = useQueryClient();
  const impersonatedUserId = useImpersonationSelector(selectImpersonatedUserId);
  const preferencesQuery = allPreferencesQuery(impersonatedUserId);
  const preferencesQueryKey = preferencesQuery.queryKey;
  const [savedPreferences, setSavedPreferences] = useState<UserPreferences>(
    () =>
      computeSavedPreferences(
        scope,
        queryClient.getQueryData(preferencesQueryKey)
      )
  );
  const [localPreferences, setLocalPreferences] = useState<UserPreferences>(
    () => getLocalPreferences(scope)
  );
  const [error, setError] = useState<Error | null>(null);
  const [hasHydratedFromServer, setHasHydratedFromServer] = useState(
    () => queryClient.getQueryData(preferencesQueryKey) !== undefined
  );
  const [rendered, setRendered] = useState({ scope, impersonatedUserId });
  if (
    rendered.scope !== scope ||
    rendered.impersonatedUserId !== impersonatedUserId
  ) {
    setRendered({ scope, impersonatedUserId });
    const cached = queryClient.getQueryData(preferencesQueryKey);
    setSavedPreferences(computeSavedPreferences(scope, cached));
    setLocalPreferences(getLocalPreferences(scope));
    // Scopes share one fetch; another user's may not have loaded yet.
    if (rendered.impersonatedUserId !== impersonatedUserId) {
      setHasHydratedFromServer(cached !== undefined);
    }
  }

  // Baseline for diffing a save against what was previously saved, without
  // the mutation callback closing over a stale `savedPreferences`.
  const savedPreferencesRef = useRef(savedPreferences);
  useEffect(() => {
    savedPreferencesRef.current = savedPreferences;
  }, [savedPreferences]);

  const {
    data: allServerPrefs,
    isLoading: isQueryLoading,
    error: loadError,
  } = useQuery(preferencesQuery);

  useEffect(() => {
    if (!allServerPrefs) return;
    // Cache ALL scopes to session storage at once
    // This prevents additional API calls when switching between primary filters
    Object.entries(allServerPrefs).forEach(([scopeKey, prefs]) => {
      const scopeForCache = scopeKey as PreferenceScope;
      const existing = PreferenceBrowserStorage.get(scopeForCache);
      // Merge server -> existing so local session edits always win, while new keys
      // from the server are still hydrated into the cache.
      PreferenceBrowserStorage.set(scopeForCache, {
        ...prefs,
        ...existing,
      } as UserPreferences);
    });
  }, [allServerPrefs]);

  useEffect(() => {
    if (!loadError) return;
    // If the server fetch fails, we still consider the hook "hydrated" from local defaults/storage.
    setHasHydratedFromServer(true);
  }, [loadError]);

  // Local preferences hydration with saved preferences
  useEffect(() => {
    if (!allServerPrefs) return;

    const mergedSaved = computeSavedPreferences(scope, allServerPrefs);
    setSavedPreferences(mergedSaved);

    const localScopedPrefs = PreferenceBrowserStorage.get(scope);
    const localGlobalPrefs = PreferenceBrowserStorage.get(
      PreferenceScope.GLOBAL
    );
    const mergedLocal = {
      ...mergedSaved,
      ...localGlobalPrefs,
      ...localScopedPrefs,
    } as UserPreferences;

    PreferenceBrowserStorage.set(scope, mergedLocal);
    setLocalPreferences(mergedLocal);
    setHasHydratedFromServer(true);
  }, [allServerPrefs, scope]);

  const { mutateAsync: savePreferenceToServer, isPending: isSyncing } =
    useMutation({
      mutationFn: (prefs: Partial<UserPreferences>) =>
        savePreferencesForScope(scope, prefs),
      onSuccess: async (_result, variables) => {
        // The save payload is the full scope-filtered store, so report only
        // the keys whose values actually differ from the saved baseline.
        const previous = normalizePreferencesForDiff(
          savedPreferencesRef.current
        );
        const next = normalizePreferencesForDiff({
          ...savedPreferencesRef.current,
          ...variables,
        });
        const changedKeys = jsonKeysWithDifferentValues(next, previous).filter(
          (key) => key in variables
        );
        if (changedKeys.length > 0) {
          mixpanel.track(PageNavTrackingEvents.PREFERENCE_CHANGED, {
            change_type: 'default',
            preference_scope: scope,
            preference_keys: changedKeys,
            ...trackablePreferenceValues(changedKeys, variables),
          });
        }
        // Advance the baseline synchronously: two saves resolving before the
        // mirror effect runs would otherwise diff the second against the
        // pre-first baseline and re-report the first save's keys.
        savedPreferencesRef.current = {
          ...savedPreferencesRef.current,
          ...variables,
        };
        setSavedPreferences((prev) => ({ ...prev, ...variables }));
        await queryClient.invalidateQueries({ queryKey: preferencesQueryKey });
      },
      onError: (err: unknown) => {
        setError(err as Error);
      },
    });

  // Sync local changes to server
  const syncWithServer = async () => {
    try {
      setError(null);

      const stored = PreferenceBrowserStorage.get(scope);
      const storedGlobal = PreferenceBrowserStorage.get(PreferenceScope.GLOBAL);
      const merged = { ...storedGlobal, ...stored };
      const relevantForScope = filterPreferencesForScope(scope, merged);
      if (Object.keys(relevantForScope).length > 0) {
        await savePreferenceToServer(relevantForScope);
      } else {
        // Consumers also use this as a "refresh saved defaults" signal.
        // If there is nothing relevant to save for this scope, still refresh server state.
        await queryClient.invalidateQueries({ queryKey: preferencesQueryKey });
      }
    } catch (err) {
      setError(err as Error);
      console.error('Failed to sync preferences to server:', err);
      throw err;
    }
  };

  // Listen for local storage changes on the active scope AND the GLOBAL scope,
  // since effective preferences merge GLOBAL + scoped values.
  // biome-ignore lint/correctness/useExhaustiveDependencies: impersonatedUserId re-subscribes — the storage keys are namespaced per impersonated user.
  useEffect(() => {
    const unsubscribeScoped = PreferenceBrowserStorage.listenForChanges(
      scope,
      () => setLocalPreferences(getLocalPreferences(scope))
    );

    let unsubscribeGlobal: (() => void) | undefined;
    if (scope !== PreferenceScope.GLOBAL) {
      unsubscribeGlobal = PreferenceBrowserStorage.listenForChanges(
        PreferenceScope.GLOBAL,
        () => setLocalPreferences(getLocalPreferences(scope))
      );
    }

    // Listen for save-sync notifications to refresh savedPreferences across instances
    const unsubscribeSaveSync = StorageSubscribe.subscribe(
      StorageKind.Session,
      getPreferenceSavedSyncKey(scope),
      () => {
        // Recalculate from local (server-aligned) state
        setSavedPreferences(getLocalPreferences(scope));
      }
    );

    return () => {
      unsubscribeScoped();
      unsubscribeSaveSync();
      if (unsubscribeGlobal) unsubscribeGlobal();
    };
  }, [scope, impersonatedUserId]);

  return {
    preferences: localPreferences,
    savedPreferences,
    isInitialLoading: isQueryLoading || !hasHydratedFromServer,
    isSyncing,
    error: loadError || error || null,
    syncWithServer,
  };
}
